import type { ReactElement } from 'react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import type { ComboBoxItem } from 'atom-components/combo-boxes/types';
import SingleComboBox from 'atom-components/form-fields-for-hook-form/single-combo-box';
import Label from 'atom-components/label';
import MarkStatusBadge from 'feature-components/badges/mark-status-badge';
import { markStatusDisplayName } from 'pages/marking/helpers/presentation.helpers';
import { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';

type Props = {
  canAdministrateMark: boolean;
};

export default function StatusSelect({ canAdministrateMark }: Props): ReactElement {
  const { control, setValue, watch } = useFormContext();

  const comboBoxLabel = 'Status';
  const comboBoxName = 'status';

  const selectableStatuses: ComboBoxItem[] = Object.values(MarkStatus).map((status) => ({
    id: status.toString(),
    name: markStatusDisplayName(status),
    badge: <MarkStatusBadge>{status}</MarkStatusBadge>
  }));

  const flagForAttention = watch('flagForAttention');

  return (
    <div>
      <Label htmlFor={comboBoxName}>{comboBoxLabel}</Label>
      <SingleComboBox
        id={comboBoxName}
        name={comboBoxName}
        control={control}
        allItems={selectableStatuses}
        disabled={!canAdministrateMark}
        clearable={false}
        onChange={(item): void => {
          const markStatus = item?.id;
          setValue(comboBoxName, markStatus, {
            shouldValidate: true,
            shouldTouch: true
          });

          // If flag for attention is checked, and the admin has changing the status away from being
          // in a reviewed state, automatically uncheck the flag for attention checkbox as well.
          // This ensures that the mark doesn't go back into review again unintentionally if a
          // marker submits the mark again in the future:
          const finishedReviewingStatuses = [
            MarkStatus.ReturnedForMarking,
            MarkStatus.Reassigned,
            MarkStatus.FinishedSuccess,
            MarkStatus.FinishedFailure
          ];
          if (
            canAdministrateMark &&
            flagForAttention &&
            finishedReviewingStatuses.find((frs) => frs === markStatus)
          ) {
            setValue('flagForAttention', false);
          }
        }}
      />
    </div>
  );
}
